import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, Row, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import type {
  AnalyticsAppGroupCount,
  AnalyticsUsersOrganizationsSummary,
} from '../types';

const { Title, Text } = Typography;

type DailyChartItem = {
  date: string;
  label: string;
  count: number;
};

function formatChartDate(date: string) {
  const parsed = new Date(`${date}T00:00:00`);
  return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
}

function buildDailyItems(
  daily: AnalyticsUsersOrganizationsSummary['daily'],
  field: 'registeredUsers' | 'activeUsers'
): DailyChartItem[] {
  const today = new Date().toISOString().slice(0, 10);
  return daily.map((item) => ({
    date: item.date,
    label: item.date === today ? 'Today' : formatChartDate(item.date),
    count: item[field] || 0,
  }));
}

function ColumnChart({ title, items }: { title: string; items: DailyChartItem[] }) {
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <Card>
      <Title level={4}>{title}</Title>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200 }}>
        {items.map((item) => {
          const height = Math.max((item.count / max) * 180, 2);
          return (
            <div key={item.date} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div
                title={`${item.date}: ${item.count}`}
                style={{
                  height,
                  background: 'linear-gradient(180deg, #f46524 0%, #f08f4b 100%)',
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                }}
              >
                {item.count > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -18,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#f46524',
                    }}
                  >
                    {item.count}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function MetricCard({
  title,
  value,
  description,
}: {
  title: string;
  value: number;
  description: string;
}) {
  return (
    <Card>
      <Text type="secondary">{title}</Text>
      <Title level={2} style={{ margin: 0 }}>{value.toLocaleString()}</Title>
      <Text type="secondary">{description}</Text>
    </Card>
  );
}

function CountTable({
  title,
  rows,
}: {
  title: string;
  rows: AnalyticsAppGroupCount[];
}) {
  const columns: ColumnsType<AnalyticsAppGroupCount> = [
    {
      title: 'App Group',
      dataIndex: 'appGroupName',
      key: 'appGroupName',
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      width: 120,
      render: (value) => Number(value || 0).toLocaleString(),
    },
  ];

  return (
    <Card title={title}>
      <Table
        rowKey="appGroupId"
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: 'No active records' }}
      />
    </Card>
  );
}

export default function AnalyticsUsersOrganizationsPage() {
  const [summary, setSummary] = useState<AnalyticsUsersOrganizationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    loadSummary();
  }, [ignoreAnnea]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const data = await api.analytics.usersOrganizations({ ignoreAnnea });
      setSummary(data);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('[AnalyticsUsersOrganizations] Failed to load summary', error);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const registeredDaily = useMemo(
    () => buildDailyItems(summary?.daily || [], 'registeredUsers'),
    [summary]
  );
  const activeDaily = useMemo(
    () => buildDailyItems(summary?.daily || [], 'activeUsers'),
    [summary]
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Users and Organizations Analytics</Title>
          <Text type="secondary">
            Account and subscription overview{lastUpdated ? ` · Updated ${lastUpdated}` : ''}
          </Text>
        </div>
        <Space>
          <Checkbox
            checked={ignoreAnnea}
            onChange={(event) => setIgnoreAnnea(event.target.checked)}
          >
            Ignore Annea users
          </Checkbox>
          <Button onClick={() => void loadSummary()} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <ColumnChart title="Registered Users per Day (Last 30 Days)" items={registeredDaily} />
        </Col>
        <Col xs={24} lg={12}>
          <ColumnChart title="Logged-in Users per Day (Last 30 Days)" items={activeDaily} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Registered Users"
            value={summary?.users.registered || 0}
            description="Total user accounts"
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Users with Paid Subscription"
            value={summary?.users.paid.total || 0}
            description="Distinct users with paid personal subscriptions or assigned org seats"
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Users with Trial"
            value={summary?.users.trial.total || 0}
            description="Distinct users with personal trials or assigned org trial seats"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <CountTable title="Users with Paid Subscription by App Group" rows={summary?.users.paid.byAppGroup || []} />
        </Col>
        <Col xs={24} lg={12}>
          <CountTable title="Users with Trial by App Group" rows={summary?.users.trial.byAppGroup || []} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Organizations"
            value={summary?.organizations.registered || 0}
            description="Total organizations"
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Organizations with Paid Subscription"
            value={summary?.organizations.paid.total || 0}
            description="Distinct organizations with active paid subscriptions"
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Organizations with Trial"
            value={summary?.organizations.trial.total || 0}
            description="Distinct organizations with active trials"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <CountTable title="Organizations with Paid Subscription by App Group" rows={summary?.organizations.paid.byAppGroup || []} />
        </Col>
        <Col xs={24} lg={12}>
          <CountTable title="Organizations with Trial by App Group" rows={summary?.organizations.trial.byAppGroup || []} />
        </Col>
      </Row>
    </div>
  );
}
